// ThemeArchivePanel — R5 personal-archive half (pt89X, Feb 20, 2026, Randy).
//
// New AI Calendar sub-button panel that lists every theme the current
// owner has applied to any of their silos. From here they can:
//   • one-tap "Activate" a past theme onto the CURRENT silo
//   • download a theme's JSON envelope to their machine
//   • upload a downloaded JSON to re-apply on this silo
//
// The public sharing / marketplace / search half of the R5 spec stays
// deferred pending Randy's moderation decision — no UI for that here.
//
// Data source: GET /api/calendar/themes/mine.
// Import target: POST /api/calendar/silos/{silo}/calendar-theme/import.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, Spinner, ArrowClockwise, Trash, PencilSimple, CheckCircle, XCircle, DownloadSimple, UploadSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useCalendarTheme } from "@/lib/CalendarThemeProvider";
import ThemeWordEditorModal from "@/components/ThemeWordEditorModal";

// Feb 2026 (Randy pt-theme-uptodate) — human-friendly "N minutes ago"
// for the "Up to date" pill on the active theme. Deliberately terse so
// the pill fits inside the theme row's already-crowded flex line.
function agoLabel(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Feb 2026 (Randy pt-theme-freshness) — snapshot is "stale" when the
// packed-away content memory is older than 12 hours (or missing entirely).
// Green ✓ = fresh; red ✕ = stale. Matches the "content memory" gut-feel:
// same-day work is safe; anything overnight+ deserves an interim snapshot.
const STALE_MS = 12 * 60 * 60 * 1000;
function isSnapshotStale(iso) {
  if (!iso) return true;
  const ms = Date.now() - new Date(iso).getTime();
  return !Number.isFinite(ms) || ms > STALE_MS;
}

// Feb 26, 2026 (Randy pt-theme-archive-title-timestamp) — every archive
// row needs a visible date/time distinguisher; source_prompt alone is not
// enough when two themes share similar prompts (or none at all). Displays
// short local timestamp; falls back to blank if the ISO is missing.
function formatThemeStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function ThemeArchivePanel({ siloId }) {
  const { reload } = useCalendarTheme();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [wordEditorOpen, setWordEditorOpen] = useState(false);
  // Feb 2026 (Randy pt-theme-uptodate) — the active theme's last
  // packed-away-content snapshot timestamp, plus a "snapshotting" flag
  // for the manual "Snapshot now" button on that row.
  const [snapSavedAt, setSnapSavedAt] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);
  // Jul 30, 2026 (Randy pt89c-xxix) — JSON Download / Upload restored.
  // Randy explicitly reversed the earlier removal because he now has
  // real cross-environment use cases: pull a theme JSON from production,
  // re-import it into preview; or share a theme file between two users.
  // Endpoints (`/export` + `/import`) never went away — only the buttons
  // did. Payload shape is `{$schema, silo_name_at_export, theme_map,
  // theme_meta, exported_at}` — a portable envelope /import accepts
  // as-is or as a raw theme_map dict.
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  // Feb 26, 2026 (Randy pt-theme-full-kid-mirror) — after a successful
  // import, if the source silo had adult collaborators (email family
  // members / parent-pill members), show a modal listing them so the
  // owner knows to re-invite manually. Adults are NOT auto-imported
  // (cross-environment identity is too messy to auto-reconcile).
  const [adultCollabHint, setAdultCollabHint] = useState(null);
  const fileInputRef = useRef(null);

  const exportJSON = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/calendar/silos/${siloId}/calendar-theme/export`);
      const payload = r.data || {};
      const themeName = payload.theme_meta?.name || payload.silo_name_at_export || "theme";
      const stamp = new Date().toISOString().slice(0, 10);
      const safeName = String(themeName).replace(/[^\w\-]+/g, "_").slice(0, 40);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mysilo-theme-${safeName}-${stamp}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Theme JSON downloaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't export theme.");
    } finally { setExporting(false); }
  };

  const importJSON = () => fileInputRef.current?.click();

  const onImportFile = async (evt) => {
    const file = evt.target.files?.[0];
    // Reset the input so re-picking the same file re-fires change.
    if (evt.target) evt.target.value = "";
    if (!file) return;
    if (file.size > 1024 * 1024) { // 1 MB is more than any real theme JSON needs
      toast.error("File too large — theme JSON should be under 1 MB.");
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      let parsed;
      try { parsed = JSON.parse(text); }
      catch { toast.error("That file isn't valid JSON."); return; }
      // Accept: v2 envelope (theme_map + content), v1 envelope (theme_map only), or a raw theme_map dict.
      const looksLikeEnvelope = parsed && typeof parsed === "object" && (
        (parsed.theme_map && typeof parsed.theme_map === "object") ||
        (parsed.content && typeof parsed.content === "object")
      );
      const looksLikeRawMap = parsed && typeof parsed === "object" && !parsed.theme_map && !parsed.content && Object.values(parsed).every((v) => typeof v === "string");
      if (!looksLikeEnvelope && !looksLikeRawMap) {
        toast.error("That JSON doesn't look like a My Silo theme file.");
        return;
      }
      if (!window.confirm(
        `Import "${parsed.theme_meta?.name || parsed.silo_name_at_export || "this theme"}" onto this silo?\n\n` +
        (parsed.content
          ? "This is a FULL-content JSON. Importing will REPLACE this silo's current events, chores, rewards, staging alerts and describers with the ones inside the file. The current theme's content packs away first, so switching back to it isn't destructive. Kid avatar emojis reset to \u2b50 (mapped where kid IDs match)."
          : "The current theme's content packs away into its memory, and this theme's content is restored (or starts fresh if it's new here). Kid avatar emojis reset to \u2b50. Nothing is permanently deleted.")
      )) return;
      const body = looksLikeEnvelope
        ? { theme_map: parsed.theme_map || {}, theme_meta: parsed.theme_meta || {}, content: parsed.content }
        : { theme_map: parsed };
      const resp = await api.post(`/calendar/silos/${siloId}/calendar-theme/import`, body);
      toast.success("Theme JSON imported.");
      // Adult collab hint — surface a follow-up modal listing invitees
      // the owner will need to re-add manually. Kids/avatars/household
      // config all rode with the JSON; adult identity did not.
      const hint = (resp.data?.hydrated?.adult_collaborators_hint) || [];
      if (Array.isArray(hint) && hint.length > 0) {
        setAdultCollabHint(hint);
      }
      await reload();
      // Re-fetch the archive list so the newly-applied theme shows as active.
      try {
        const rr = await api.get("/calendar/themes/mine");
        setThemes(rr.data?.themes || []);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't import theme.");
    } finally { setImporting(false); }
  };

  const loadSnapStatus = async () => {
    try {
      const r = await api.get(`/calendar/silos/${siloId}/calendar-theme/snapshot-status`);
      setSnapSavedAt(r.data?.snapshot_saved_at || null);
    } catch { /* non-fatal — pill just won't render */ }
  };

  const snapshotNow = async () => {
    setSnapshotting(true);
    try {
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/snapshot-now`);
      setSnapSavedAt(r.data?.snapshot_saved_at || new Date().toISOString());
      toast.success("Snapshot taken — your current content is safely stored.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't snapshot.");
    } finally { setSnapshotting(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/calendar/themes/mine");
        if (!alive) return;
        setThemes(r.data?.themes || []);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Couldn't load your themes.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    loadSnapStatus();
    return () => { alive = false; };
  }, [siloId]);

  const activate = async (row) => {
    // The built-in "Family (default)" card has no theme_map — activating it
    // means resetting to defaults (a non-destructive theme switch that brings
    // back whatever content the Family theme last held).
    if (row.kind === "default") {
      if (!window.confirm(
        "Switch this silo back to the Family (default) theme?\n\n" +
        "The current theme's calendar, chores and rewards pack away into its memory, and the Family theme's own content comes back. Nothing is permanently deleted."
      )) return;
      setActivatingId(row.id);
      try {
        await api.delete(`/calendar/silos/${siloId}/calendar-theme`);
        toast.success("Family (default) theme activated.");
        await reload();
      } catch (e) { toast.error(e.response?.data?.detail || "Couldn't activate."); }
      finally { setActivatingId(null); }
      return;
    }
    if (!row?.theme_map) return;
    if (!window.confirm(
      `Activate "${row.name || row.silo_name}" on this silo?\n\n` +
      "The current theme's content packs away into its memory, and this theme's content is restored (or starts fresh if it's new here). Kid avatar emojis reset to \u2b50. Nothing is permanently deleted."
    )) return;
    setActivatingId(row.id);
    try {
      await api.post(`/calendar/silos/${siloId}/calendar-theme/import`, {
        theme_map: row.theme_map,
        theme_meta: { ...(row.theme_meta || {}), name: row.name || row.theme_meta?.name, imported_from_silo: row.silo_name },
      });
      toast.success("Theme activated on this silo.");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't activate."); }
    finally { setActivatingId(null); }
  };

  const removeTheme = async (row) => {
    // Feb 2026 (Randy pt-theme-trash-active) — trash now works on THREE
    // kinds of rows:
    //   • hist:*   → archived history entry, delete directly
    //   • active:* → currently applied theme; first switch back to Family
    //                default (which snapshots the current theme's content
    //                into its memory automatically), then delete that
    //                fresh history entry so the theme+its memory are gone.
    // The default "Family" row itself can never be deleted.
    const histMatch = /^hist:([^:]+):(.+)$/.exec(row.id || "");
    const isActive = row.id === `active:${siloId}`;
    if (!histMatch && !isActive) return;
    if (row.kind === "default") return;
    if (!window.confirm(
      `Permanently delete "${row.name || row.theme_meta?.source_prompt?.slice(0, 40) || "this theme"}"?\n\n` +
      (isActive
        ? "This will first switch this silo back to the Family (default) theme, then permanently remove this theme AND its packed-away content memory. Cannot be undone."
        : "This is the ONLY action that permanently removes a theme AND its packed-away content memory (its saved events, chores and rewards). This can't be undone.")
    )) return;
    setDeletingId(row.id);
    try {
      if (isActive) {
        // Switch to default first — this snapshots the theme we're about
        // to delete into theme_history under a fresh entry_id, then
        // restores the Family-default content.
        await api.delete(`/calendar/silos/${siloId}/calendar-theme`);
        // Reload the archive to discover the new history entry_id for
        // the theme we just switched away from, so we can delete it.
        const r = await api.get("/calendar/themes/mine");
        const themes = r.data?.themes || [];
        // Best-match: same theme_map hash if present, else the newest
        // hist:* entry for this silo (the one we just archived).
        const candidates = themes.filter((t) => (t.id || "").startsWith(`hist:${siloId}:`));
        candidates.sort((a, b) => (b.theme_meta?.applied_at || "").localeCompare(a.theme_meta?.applied_at || ""));
        const target = candidates[0];
        if (target) {
          const m2 = /^hist:([^:]+):(.+)$/.exec(target.id);
          if (m2) await api.delete(`/calendar/silos/${m2[1]}/calendar-theme/history/${m2[2]}`);
        }
        setThemes(themes.filter((t) => t.id !== target?.id));
        toast.success("Theme deleted; switched to Family (default).");
        await reload();
      } else {
        const sid = histMatch[1], entryId = histMatch[2];
        await api.delete(`/calendar/silos/${sid}/calendar-theme/history/${entryId}`);
        setThemes((cur) => cur.filter((t) => t.id !== row.id));
        toast.success("Theme deleted.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete."); }
    finally { setDeletingId(null); }
  };

  // Feb 2026 (Randy pt-uptodate-visibility) — the "Up to date" pill was
  // only rendering on rows shaped `active:${siloId}`, which only exists
  // when the current silo has a CUSTOM theme applied. Silos on the
  // Family default get NO `active:` row, so the pill would silently
  // disappear. Fix: also treat the default row as "the active one" when
  // no active row exists for this silo — that is, whenever there's no
  // `active:${siloId}` in the themes list.
  const hasCustomActive = themes.some((t) => t.id === `active:${siloId}`);
  const rowIsCurrentlyActive = (row) => (
    row.id === `active:${siloId}` || (!hasCustomActive && row.kind === "default")
  );

  if (loading) {
    return <div className="text-xs text-[#6B6A68] flex items-center gap-1.5 py-3" data-testid="theme-archive-loading"><Spinner size={12} className="animate-spin" /> Loading your themes…</div>;
  }
  if (err) {
    return <div className="text-xs text-[#A55A48] italic py-2" data-testid="theme-archive-error">{err}</div>;
  }

  return (
    <div className="space-y-2" data-testid="theme-archive-panel">
      <div className="flex items-center gap-2">
        <Palette size={14} weight="duotone" className="text-[#8A6AC1]" />
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] flex-1">
          Your saved themes <span className="normal-case text-[#8A8A88] italic">({themes.length})</span>
        </div>
        {/* Feb 2026 (Randy pt-theme-download-per-row) — the header
            "Download JSON" button moved to a per-row icon (next to the
            trash) so each theme in the list gets its own download. The
            Upload picker stays here since it applies to any file. */}
        <button onClick={importJSON} disabled={importing}
          data-testid="theme-archive-import-json-btn"
          title="Upload a previously-exported theme JSON file. Applies it to this silo the same way tapping Activate does."
          className="text-[10px] px-2 py-1 rounded border hairline text-[#3A3A38] hover:bg-black/5 flex items-center gap-1 disabled:opacity-50">
          {importing ? <Spinner size={11} className="animate-spin" /> : <UploadSimple size={11} weight="duotone" />}
          Upload JSON
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={onImportFile}
          data-testid="theme-archive-import-json-file"
          className="hidden"
        />
      </div>

      {/* Feb 2026 (Randy pt-theme-uptodate-column) — column-header row so the
          "Up to date" freshness indicator has a label. Sits above the table
          and mirrors the row layout so the dot+✓/✕ column aligns visually.
          Feb 2026 (Randy pt-theme-mobile) — hidden on mobile; the stacked
          two-line row layout below doesn't need column headers. */}
      {themes.length > 0 && (
        <div className="hidden sm:flex items-center gap-2 px-2.5 pb-0.5 text-[9px] uppercase tracking-[0.14em] text-[#8A8A88]" data-testid="theme-archive-header-row">
          <span className="w-[54px] shrink-0">Kind</span>
          <span className="flex-1">Theme</span>
          <span className="w-[92px] shrink-0 text-right pr-1" title="Fresh (green ✓) when this silo's packed-away content memory was updated in the last 12 hours. Stale (red ✕) means take an interim Snapshot before deep edits.">Up to date</span>
          <span className="min-w-[196px] shrink-0 text-right pr-1">Actions</span>
        </div>
      )}

      {themes.length === 0 ? (
        <div className="text-[11px] text-[#6B6A68] italic bg-white border hairline rounded p-3">
          No themes yet — apply one via the Re-Theme button in the AI Calendar header.
        </div>
      ) : (
        <div className="space-y-1" data-testid="theme-archive-list">
          {themes.map((row) => {
            const isCurrent = rowIsCurrentlyActive(row);
            const isFamilyDefaultRow = row.kind === "default";
            // Editing words is only meaningful when THIS row is the silo's
            // active theme. On the Family row, that's only when the silo
            // has no custom theme_map applied yet.
            const showEditWords = isCurrent;
            // The Family (default) row shows "Reset to default" when the
            // silo currently has a custom theme applied (item D). On the
            // Family row itself when it's already current, no reset is
            // needed — the green "Current silo" chip handles that state.
            const showResetToDefault = isFamilyDefaultRow && !isCurrent;
            // Download JSON lives per-row now (item B) — but the /export
            // endpoint only knows how to export the ACTIVE theme, so we
            // only render the download icon on the current silo's row.
            const showDownload = isCurrent && !isFamilyDefaultRow;
            const stale = isSnapshotStale(snapSavedAt);
            const showFreshness = isCurrent;
            return (
            /* Feb 2026 (Randy pt-theme-mobile) — mobile-stacked, desktop-tabular.
               Mobile: two-line layout (Kind+Name on line 1, Freshness+Actions on
               line 2) so the theme NAME (most important thing per Randy) gets
               the full row width and never truncates to nothing. Desktop keeps
               the single-row 4-column tabular layout aligned to the header. */
            <div key={row.id}
              className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2 bg-white border hairline rounded px-2.5 py-1.5"
              data-testid={`theme-archive-row-${row.id}`}>
              {/* Top cluster on mobile / left half on desktop — Kind + Name */}
              <div className="flex items-center gap-2 min-w-0 sm:flex-1">
                <span className={`w-[54px] shrink-0 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border text-center ${row.kind === "active" ? "bg-[#F4F8F5] text-[#2F6B3A] border-[#B9D4BE]" : row.kind === "default" ? "bg-[#F1ECF9] text-[#6A4AA0] border-[#D9CBEF]" : "bg-black/5 text-[#6B6A68] border-black/10"}`}>
                  {row.kind === "active" ? "Active" : row.kind === "default" ? "Default" : "Archived"}
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-sm text-[#1C1B1A] truncate flex items-center gap-1.5">
                    <span className="truncate">
                      {row.name ? <strong>{row.name}</strong> : (row.theme_meta?.source_prompt?.slice(0, 40) || "Untitled")}
                    </span>
                    {/* Feb 26, 2026 (Randy pt-theme-archive-title-timestamp)
                        — small greyed timestamp so two archives never look
                        identical. Backend already resolves `applied_at`
                        for both: theme_meta.generated_at for active,
                        archived_at for archived. */}
                    {(() => {
                      const stamp = formatThemeStamp(row.applied_at);
                      return stamp
                        ? <span className="shrink-0 text-[10px] text-[#8A8A88] tabular-nums" data-testid={`theme-row-stamp-${row.id}`}>· {stamp}</span>
                        : null;
                    })()}
                  </div>
                  <div className="text-[10px] text-[#6B6A68] truncate">
                    from <em>{row.silo_name}</em>
                    {row.theme_meta?.tone && <span className="ml-1 uppercase tracking-[0.14em]">· {row.theme_meta.tone}</span>}
                  </div>
                </span>
              </div>
              {/* Bottom cluster on mobile / right half on desktop — Freshness + Actions */}
              <div className="flex items-center gap-1.5 justify-end flex-wrap sm:flex-nowrap sm:gap-2">
                {/* Item A — freshness column: green dot + ✓ (fresh) / red dot + ✕ (stale),
                    clickable on the current row to trigger an interim snapshot. */}
                <span className="sm:w-[92px] sm:shrink-0 flex items-center sm:justify-end sm:pr-1">
                  {showFreshness ? (
                    <button onClick={snapshotNow} disabled={snapshotting}
                      data-testid="theme-snapshot-now-btn"
                      title={snapSavedAt
                        ? `Content memory last packed ${agoLabel(snapSavedAt)}${stale ? " — consider snapshotting now" : ""}. Tap to snapshot again.`
                        : "No packed-away content yet — tap to snapshot now (auto-snaps on next theme switch)."}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border disabled:opacity-50 ${stale ? "bg-[#FBEBE4] text-[#A55A48] border-[#EBC7BB] hover:bg-[#F6DAC7]" : "bg-[#F4F8F5] text-[#2F6B3A] border-[#B9D4BE] hover:bg-[#E8F1EA]"}`}>
                      {snapshotting
                        ? <Spinner size={9} className="animate-spin" />
                        : (stale
                            ? <XCircle size={11} weight="fill" className="text-[#C16E5A]" />
                            : <CheckCircle size={11} weight="fill" className="text-[#2F6B3A]" />)}
                      <span className="tabular-nums">{agoLabel(snapSavedAt)}</span>
                    </button>
                  ) : (
                    // Only show the dash placeholder on desktop — on mobile the
                    // freshness cell just collapses when it has nothing to show.
                    <span className="hidden sm:inline text-[10px] text-[#B9B8B6]" aria-hidden="true">—</span>
                  )}
                </span>
                {/* Actions cluster */}
                <span className="sm:min-w-[196px] sm:shrink-0 flex items-center justify-end gap-1 flex-wrap">
                  {showEditWords && (
                    <button onClick={() => setWordEditorOpen(true)}
                      data-testid={`theme-words-edit-btn-${row.id}`}
                      title="Hand-edit any of this theme's words — search, batch-change, rename. Never touches your content."
                      className="text-[10px] p-1.5 rounded border hairline text-[#3A3A38] hover:bg-black/5 flex items-center gap-1">
                      <PencilSimple size={11} weight="duotone" />
                    </button>
                  )}
                  {showDownload && (
                    <button onClick={exportJSON} disabled={exporting}
                      data-testid={`theme-archive-download-json-${row.id}`}
                      title="Download this silo's current theme (theme_map + meta + content bundle) as a JSON file."
                      className="text-[10px] p-1.5 rounded border hairline text-[#3A3A38] hover:bg-black/5 disabled:opacity-50">
                      {exporting ? <Spinner size={11} className="animate-spin" /> : <DownloadSimple size={11} weight="duotone" />}
                    </button>
                  )}
                  {(row.kind !== "default") && (
                    <button onClick={() => removeTheme(row)}
                      disabled={deletingId === row.id}
                      data-testid={`theme-archive-delete-${row.id}`}
                      title={row.id === `active:${siloId}`
                        ? "Delete this theme — first switches to Family (default), then permanently removes this theme + its content memory"
                        : "Permanently delete this archived theme AND its packed-away content memory"}
                      className="text-[10px] p-1.5 rounded text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FBEBE4] disabled:opacity-50">
                      {deletingId === row.id ? <Spinner size={11} className="animate-spin" /> : <Trash size={11} />}
                    </button>
                  )}
                  {isCurrent ? (
                    <span
                      data-testid={`theme-archive-current-chip-${row.id}`}
                      title="This theme is currently applied on this silo."
                      className="text-[10px] px-2 py-1 rounded border bg-[#F4F8F5] text-[#2F6B3A] border-[#B9D4BE] flex items-center gap-1 whitespace-nowrap">
                      <CheckCircle size={11} weight="fill" /> Current silo
                    </span>
                  ) : showResetToDefault ? (
                    <button onClick={() => activate(row)}
                      disabled={activatingId === row.id}
                      data-testid={`theme-archive-reset-default-${row.id}`}
                      title="Reset this silo back to the base Family theme — clears the name and any custom words. Your current theme packs into memory (nothing is deleted)."
                      className="text-[10px] px-2 py-1 rounded border border-[#D9CBEF] bg-[#F1ECF9] text-[#6A4AA0] hover:bg-[#E7DCF5] flex items-center gap-1 disabled:opacity-50 whitespace-nowrap">
                      {activatingId === row.id ? <Spinner size={11} className="animate-spin" /> : <ArrowCounterClockwise size={11} weight="bold" />}
                      Reset to default
                    </button>
                  ) : (
                    <button onClick={() => activate(row)}
                      disabled={activatingId === row.id}
                      data-testid={`theme-archive-activate-${row.id}`}
                      title="Apply this theme onto the current silo"
                      className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1 disabled:opacity-50 whitespace-nowrap">
                      {activatingId === row.id ? <Spinner size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />}
                      Activate
                    </button>
                  )}
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}
      {wordEditorOpen && <ThemeWordEditorModal siloId={siloId} onClose={() => setWordEditorOpen(false)} />}
      {adultCollabHint && (
        <div className="fixed inset-0 z-[220] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAdultCollabHint(null)} data-testid="adult-collab-hint-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 border hairline shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif-display text-lg text-[#1C1B1A] mb-2">Import complete — one manual step left</h3>
            <p className="text-xs text-[#6B6A68] mb-3 leading-relaxed">
              Kids, avatars, and all household settings came across cleanly.
              But adult collaborators (email family members, parent-pill members) can&rsquo;t be auto-imported —
              cross-environment identity is too complex to reconcile safely. You&rsquo;ll need to re-invite each one manually:
            </p>
            <ul className="border hairline rounded-md divide-y divide-[#E9E7E1] mb-4 max-h-[40vh] overflow-y-auto" data-testid="adult-collab-hint-list">
              {adultCollabHint.map((c, i) => (
                <li key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#1C1B1A] truncate">{c.name || c.email || "Family member"}</div>
                    {c.email && c.name && <div className="text-[10px] text-[#6B6A68] truncate">{c.email}</div>}
                  </div>
                  {c.is_parent && (
                    <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F8F1E2] border border-[#C19A4A] text-[#8A6A24]">Parent</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button onClick={() => setAdultCollabHint(null)} data-testid="adult-collab-hint-close"
                className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E]">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
